// The fake api's station releases (spec/14 §12, F-STN-12, spec/10 §8), past the HQ guard in `fake-server.ts`
// (`hq:releases`): the registered releases, each channel's rollout and the fleet's counts. Bodies are checked with
// the contract's own schemas, and it keeps the real service's rules: a registration's manifest is the JWS payload
// (a signature segment of `bad` stands for one that does not verify → `422`), a version registered twice is `409`,
// a rollout's target must be a registered release (`422`), and halting a release or a rollout stores the flag.
//
// And the boot server releases on the same page (F-BOOT-23): registered the same way (a boot server manifest — its
// `product` and audience — or `422`), and made current on a channel (`422` for a version not registered).

import type {
  BootServerRelease,
  BootServerReleaseChannelState,
  BootServerReleaseManifest,
  StationFleet,
  StationRelease,
  StationReleaseManifest,
  StationRollout,
} from '@clavian/api-client';
import {
  BootServerReleaseManifestSchema,
  RegisterBootServerReleaseSchema,
  RegisterStationReleaseSchema,
  SetBootServerReleaseChannelSchema,
  SetStationRolloutSchema,
  StationReleaseManifestSchema,
  UpdateStationReleaseSchema,
} from '@clavian/contracts';

import type { CatalogueHelpers } from './fake-hq-catalogue';
import type { FakeResponse } from './fake-server';

const NOW = '2026-09-30T09:00:00.000Z';

function base64url(text: string): string {
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64url(part: string): string {
  const padded = part.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
}

/** A manifest the fake accepts: a well-formed release of `version`. */
export function releaseManifest(version: string, overrides: Partial<StationReleaseManifest> = {}): StationReleaseManifest {
  return {
    version,
    channel: 'stable',
    msi: { url: `https://releases.clavian.test/station/${version}/Clavian.Station.msi`, sha256: 'a'.repeat(64), size: 94_371_840 },
    kiosk: { version: `${version.split('.').slice(0, 2).join('.')}.7`, url: `https://releases.clavian.test/kiosk/${version}.zip`, sha256: 'b'.repeat(64), size: 12_582_912 },
    minServiceVersion: null,
    releasedAt: '2026-09-28T10:00:00.000Z',
    ...overrides,
  };
}

/** A compact JWS carrying `manifest`, as the release tool prints it. `signature: 'bad'` makes one that does not verify. */
export function signedManifest(manifest: StationReleaseManifest, signature = 'c2lnbmF0dXJl'): string {
  return `${base64url(JSON.stringify({ alg: 'EdDSA', kid: 'release-2026' }))}.${base64url(JSON.stringify({ ...manifest, aud: 'clavian-station-release' }))}.${signature}`;
}

/** A boot server manifest the fake accepts: a well-formed release of `version`. */
export function bootReleaseManifest(version: string, overrides: Partial<BootServerReleaseManifest> = {}): BootServerReleaseManifest {
  return {
    product: 'clavian-boot-server',
    version,
    channel: 'stable',
    msi: { url: `https://releases.clavian.test/boot/releases/${version}/ClavianBootServer-${version}.msi`, sha256: 'c'.repeat(64), size: 125_829_120 },
    minVersion: null,
    releasedAt: '2026-10-14T10:00:00.000Z',
    ...overrides,
  };
}

/** A compact JWS carrying a boot server `manifest`. `signature: 'bad'` makes one that does not verify. */
export function signedBootManifest(manifest: BootServerReleaseManifest, signature = 'c2lnbmF0dXJl'): string {
  return `${base64url(JSON.stringify({ alg: 'EdDSA', kid: 'release-2026' }))}.${base64url(JSON.stringify({ ...manifest, aud: 'clavian-boot-server-release' }))}.${signature}`;
}

function bootRelease(manifest: BootServerReleaseManifest, currentOn: BootServerRelease['currentOn'] = []): BootServerRelease {
  return { version: manifest.version, channel: manifest.channel, manifest, keyId: 'release-2026', currentOn: [...currentOn], createdAt: manifest.releasedAt };
}

function release(manifest: StationReleaseManifest, halted = false): StationRelease {
  return { version: manifest.version, channel: manifest.channel, manifest, keyId: 'release-2026', halted, createdAt: manifest.releasedAt };
}

export interface FakeReleasesOptions {
  /** Nothing registered, no rollout set, and no station reporting — the feature's first day. */
  readonly empty?: boolean;
}

export function createFakeHqReleases(options: FakeReleasesOptions = {}) {
  const state = {
    releases: options.empty
      ? ([] as StationRelease[])
      : [
          release(releaseManifest('1.10.100.0', { channel: 'beta', releasedAt: '2026-09-29T10:00:00.000Z' })),
          release(releaseManifest('1.9.41234.0', { releasedAt: '2026-09-27T10:00:00.000Z' })),
          release(releaseManifest('1.9.41000.0', { releasedAt: '2026-09-20T10:00:00.000Z' }), true),
        ],
    rollouts: options.empty
      ? ([
          { channel: 'stable', targetVersion: null, percentage: 0, halted: false, updatedAt: null },
          { channel: 'beta', targetVersion: null, percentage: 0, halted: false, updatedAt: null },
        ] as StationRollout[])
      : ([
          { channel: 'stable', targetVersion: '1.9.41234.0', percentage: 25, halted: false, updatedAt: '2026-09-28T12:00:00.000Z' },
          { channel: 'beta', targetVersion: null, percentage: 0, halted: false, updatedAt: null },
        ] as StationRollout[]),
    fleet: (options.empty
      ? { reporting: 0, versions: [], states: [] }
      : {
          reporting: 12,
          versions: [
            { clientVersion: '1.9.41000.0', stations: 8 },
            { clientVersion: '1.9.41234.0', stations: 4 },
          ],
          states: [
            { state: 'idle', stations: 10 },
            { state: 'downloading', stations: 1 },
            { state: 'rolled_back', stations: 1 },
          ],
        }) as StationFleet,
    boot: {
      releases: options.empty
        ? ([] as BootServerRelease[])
        : [bootRelease(bootReleaseManifest('1.10.200.0'), ['stable']), bootRelease(bootReleaseManifest('1.10.100.0', { releasedAt: '2026-10-01T10:00:00.000Z' }))],
      channels: (options.empty
        ? [
            { channel: 'stable', currentVersion: null, updatedAt: null },
            { channel: 'beta', currentVersion: null, updatedAt: null },
          ]
        : [
            { channel: 'stable', currentVersion: '1.10.200.0', updatedAt: '2026-10-14T12:00:00.000Z' },
            { channel: 'beta', currentVersion: null, updatedAt: null },
          ]) as BootServerReleaseChannelState[],
    },
  };

  /** The releases with `currentOn` drawn from the channels, as the server answers. */
  const bootList = () => ({
    data: state.boot.releases.map((row) => ({ ...row, currentOn: state.boot.channels.filter((channel) => channel.currentVersion === row.version).map((channel) => channel.channel) })),
    channels: state.boot.channels,
  });

  function handle(helpers: CatalogueHelpers, method: string, pathname: string, input: unknown): FakeResponse | undefined {
    const { json, problem } = helpers;
    if (pathname === '/v1/hq/station-releases') {
      if (method === 'GET') return json(200, { data: state.releases });
      if (method === 'POST') {
        const body = RegisterStationReleaseSchema.safeParse(input);
        if (!body.success) return problem(400, 'validation_failed', 'The request is not valid.');
        const [, payload = '', signature] = body.data.signedManifest.split('.');
        if (signature === 'bad') return problem(422, 'unprocessable', 'The signature does not verify.', { detail: 'No configured release key verifies this signature.' });
        let parsed: unknown;
        try {
          parsed = JSON.parse(fromBase64url(payload));
        } catch {
          return problem(422, 'unprocessable', 'The manifest is malformed.');
        }
        const manifest = StationReleaseManifestSchema.safeParse(parsed);
        if (!manifest.success) return problem(422, 'unprocessable', 'The manifest is malformed.');
        if (state.releases.some((row) => row.version === manifest.data.version)) {
          return problem(409, 'conflict', 'A release with this version is already registered.', { detail: `${manifest.data.version} is already registered.` });
        }
        const created = { ...release(manifest.data), createdAt: NOW };
        state.releases = [created, ...state.releases];
        return json(201, created);
      }
    }
    const one = /^\/v1\/hq\/station-releases\/([^/]+)$/.exec(pathname);
    if (one !== null && method === 'PATCH') {
      const body = UpdateStationReleaseSchema.safeParse(input);
      if (!body.success) return problem(400, 'validation_failed', 'The request is not valid.');
      const found = state.releases.find((row) => row.version === decodeURIComponent(one[1] ?? ''));
      if (found === undefined) return problem(404, 'not_found', 'No release with this version.');
      found.halted = body.data.halted;
      return json(200, found);
    }
    if (pathname === '/v1/hq/station-rollouts' && method === 'GET') return json(200, { data: state.rollouts });
    const channel = /^\/v1\/hq\/station-rollouts\/(stable|beta)$/.exec(pathname);
    if (channel !== null && method === 'PUT') {
      const body = SetStationRolloutSchema.safeParse(input);
      if (!body.success) return problem(400, 'validation_failed', 'The request is not valid.');
      if (body.data.targetVersion !== null && !state.releases.some((row) => row.version === body.data.targetVersion)) {
        return problem(422, 'unprocessable', 'The target version is not a registered release.');
      }
      const updated: StationRollout = { channel: channel[1] as StationRollout['channel'], ...body.data, updatedAt: NOW };
      state.rollouts = state.rollouts.map((row) => (row.channel === updated.channel ? updated : row));
      return json(200, updated);
    }
    if (pathname === '/v1/hq/station-fleet' && method === 'GET') return json(200, state.fleet);

    if (pathname === '/v1/hq/boot-server-releases') {
      if (method === 'GET') return json(200, bootList());
      if (method === 'POST') {
        const body = RegisterBootServerReleaseSchema.safeParse(input);
        if (!body.success) return problem(400, 'validation_failed', 'The request is not valid.');
        const [, payload = '', signature] = body.data.signedManifest.split('.');
        if (signature === 'bad') return problem(422, 'unprocessable', 'The signature does not verify.', { detail: 'No configured release key verifies this signature.' });
        let parsed: unknown;
        try {
          parsed = JSON.parse(fromBase64url(payload));
        } catch {
          return problem(422, 'unprocessable', 'The manifest is malformed.');
        }
        if ((parsed as { aud?: unknown }).aud !== 'clavian-boot-server-release') {
          return problem(422, 'unprocessable', 'Not a boot server release.', { detail: 'The manifest audience is not clavian-boot-server-release: it is not a boot server release.' });
        }
        const manifest = BootServerReleaseManifestSchema.safeParse(parsed);
        if (!manifest.success) return problem(422, 'unprocessable', 'The manifest is malformed.');
        if (state.boot.releases.some((row) => row.version === manifest.data.version)) {
          return problem(409, 'conflict', 'A boot server release with this version is already registered.', { detail: `${manifest.data.version} is already registered.` });
        }
        const created = { ...bootRelease(manifest.data), createdAt: NOW };
        state.boot.releases = [created, ...state.boot.releases];
        return json(201, created);
      }
    }
    const bootChannel = /^\/v1\/hq\/boot-server-release-channels\/(stable|beta)$/.exec(pathname);
    if (bootChannel !== null && method === 'PUT') {
      const body = SetBootServerReleaseChannelSchema.safeParse(input);
      if (!body.success) return problem(400, 'validation_failed', 'The request is not valid.');
      if (body.data.currentVersion !== null && !state.boot.releases.some((row) => row.version === body.data.currentVersion)) {
        return problem(422, 'unprocessable', 'The version is not a registered boot server release.');
      }
      const updated: BootServerReleaseChannelState = { channel: bootChannel[1] as BootServerReleaseChannelState['channel'], currentVersion: body.data.currentVersion, updatedAt: NOW };
      state.boot.channels = state.boot.channels.map((row) => (row.channel === updated.channel ? updated : row));
      return json(200, updated);
    }
    return undefined;
  }

  return { state, handle };
}
